export interface UserArgument {
  id: string
  firstName: string
  lastName: string
  bio?: string
  photoUrl?: string
  notifyAndroid?: boolean
  isAdmin?: boolean
  aboutMe?: string
  goals?: string
  skills?: string
  sports?: string
  whyChallenge?: string
  hypeSong?: string
  roadTripSnacks?: string
  homeBase?: string
}

export default class User {
  readonly id: string
  readonly firstName: string
  readonly lastName: string
  readonly bio?: string
  readonly photoUrl?: string
  readonly notifyAndroid?: boolean
  readonly isAdmin?: boolean
  readonly aboutMe?: string
  readonly goals?: string
  readonly skills?: string
  readonly sports?: string
  readonly whyChallenge?: string
  readonly hypeSong?: string
  readonly roadTripSnacks?: string
  readonly homeBase?: string

  constructor(arg: UserArgument) {
    this.id = arg.id
    this.firstName = arg.firstName
    this.lastName = arg.lastName
    this.bio = arg.bio
    this.photoUrl = arg.photoUrl
    this.notifyAndroid = arg.notifyAndroid
    this.isAdmin = arg.isAdmin
    this.aboutMe = arg.aboutMe
    this.goals = arg.goals
    this.skills = arg.skills
    this.sports = arg.sports
    this.whyChallenge = arg.whyChallenge
    this.hypeSong = arg.hypeSong
    this.roadTripSnacks = arg.roadTripSnacks
    this.homeBase = arg.homeBase
  }

  static create(id: string, firstName: string, lastName: string) {
    return new User({ id, firstName, lastName })
  }

  static fromPG(data: any) {
    return new User({
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      bio: data.bio,
      photoUrl: data.photo_url,
      notifyAndroid: data.notify_android,
      isAdmin: data.is_admin,
      aboutMe: data.about_me,
      goals: data.goals,
      skills: data.skills,
      sports: data.sports,
      whyChallenge: data.why_challenge,
      hypeSong: data.hype_song,
      roadTripSnacks: data.road_trip_snacks,
      homeBase: data.home_base,
    })
  }

  toPG() {
    return {
      id: this.id,
      first_name: this.firstName,
      last_name: this.lastName,
      bio: this.bio,
      photo_url: this.photoUrl,
      notify_android: this.notifyAndroid,
      is_admin: this.isAdmin,
      about_me: this.aboutMe,
      goals: this.goals,
      skills: this.skills,
      sports: this.sports,
      why_challenge: this.whyChallenge,
      hype_song: this.hypeSong,
      road_trip_snacks: this.roadTripSnacks,
      home_base: this.homeBase ?? null,
    }
  }

  updateFirstName(firstName: string) {
    return new User({ ...this, firstName })
  }

  updateLastName(lastName: string) {
    return new User({ ...this, lastName })
  }

  updateBio(bio: string) {
    return new User({ ...this, bio })
  }

  updatePhotoUrl(photoUrl: string) {
    return new User({ ...this, photoUrl })
  }

  updateNotifyAndroid(notifyAndroid: boolean) {
    return new User({ ...this, notifyAndroid })
  }

  updateAboutMe(aboutMe: string) {
    return new User({ ...this, aboutMe })
  }

  updateGoals(goals: string) {
    return new User({ ...this, goals })
  }

  updateSkills(skills: string) {
    return new User({ ...this, skills })
  }

  updateSports(sports: string) {
    return new User({ ...this, sports })
  }

  updateWhyChallenge(whyChallenge: string) {
    return new User({ ...this, whyChallenge })
  }

  updateHypeSong(hypeSong: string) {
    return new User({ ...this, hypeSong })
  }

  updateRoadTripSnacks(roadTripSnacks: string) {
    return new User({ ...this, roadTripSnacks })
  }

  updateHomeBase(homeBase: string) {
    return new User({ ...this, homeBase })
  }

  getGoalsList(): string[] {
    if (!this.goals) return []
    return this.goals.split('\n').filter((g) => g.trim())
  }

  getSkillsList(): string[] {
    if (!this.skills) return []
    return this.skills.split('\n').filter((s) => s.trim())
  }

  getSportsList(): string[] {
    if (!this.sports) return []
    return this.sports
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s)
  }

  getRoadTripSnacksList(): string[] {
    if (!this.roadTripSnacks) return []
    return this.roadTripSnacks
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s)
  }
}
